import { useState } from "react";
import { CloudRain, Loader2, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Game } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const REASONS = [
  { value: "Washout", label: "Washout / wet weather" },
  { value: "Opponent forfeit", label: "Opponent forfeit" },
  { value: "Ground closed", label: "Ground closed" },
  { value: "Insufficient players", label: "Insufficient players" },
  { value: "Other", label: "Other" },
] as const;

interface CancelGameDialogProps {
  game: Game;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged?: () => void;
}

export function CancelGameDialog({
  game,
  open,
  onOpenChange,
  onChanged,
}: CancelGameDialogProps) {
  const isCancelled = game.status === "cancelled";
  const [reasonChoice, setReasonChoice] = useState<string>("Washout");
  const [otherReason, setOtherReason] = useState("");
  const [saving, setSaving] = useState(false);

  const finalReason =
    reasonChoice === "Other" ? otherReason.trim() : reasonChoice;

  const handleCancel = async () => {
    if (!finalReason) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("teamvote_games")
        .update({
          status: "cancelled",
          cancellation_reason: finalReason,
          voting_open: false,
        })
        .eq("id", game.id);
      if (error) throw error;
      toast.success("Match marked as cancelled");
      onChanged?.();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Failed to cancel game", err);
      toast.error("Couldn't cancel match", { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("teamvote_games")
        .update({
          status: "scheduled",
          cancellation_reason: null,
        })
        .eq("id", game.id);
      if (error) throw error;
      toast.success("Match restored to scheduled");
      onChanged?.();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Failed to restore game", err);
      toast.error("Couldn't restore match", { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isCancelled ? "Restore match?" : "Cancel this match?"}
          </DialogTitle>
          <DialogDescription>
            {isCancelled
              ? "Reopen the match so RSVPs and voting are available again."
              : "Parents will see a Cancelled banner instead of RSVP buttons. Voting is closed and stats stay clean."}
          </DialogDescription>
        </DialogHeader>

        {isCancelled ? (
          <div className="space-y-4 pt-2">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
              <p className="font-medium">
                Currently cancelled
                {game.cancellation_reason ? `: ${game.cancellation_reason}` : ""}
              </p>
              <p className="text-muted-foreground mt-1">
                Restoring won't notify parents. You can re-open voting from the
                match page once it's back on.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Keep cancelled
              </Button>
              <Button
                className="flex-1 gap-1"
                onClick={handleRestore}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                Restore
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
              <CloudRain className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground">
                Recording a cancelled match keeps your season log honest. No
                attendance is taken and it won't pull down attendance %.
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">Reason</Label>
              <RadioGroup
                value={reasonChoice}
                onValueChange={setReasonChoice}
                className="mt-2 space-y-2"
              >
                {REASONS.map((r) => (
                  <div key={r.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={r.value} id={`cg-reason-${r.value}`} />
                    <Label
                      htmlFor={`cg-reason-${r.value}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {r.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {reasonChoice === "Other" && (
                <Input
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Briefly describe the reason"
                  className="mt-3"
                  autoFocus
                />
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground flex items-start gap-2">
              <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                After cancelling, share the news with parents from the match
                page (WhatsApp / your team chat).
              </span>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                disabled={
                  saving ||
                  (reasonChoice === "Other" && !otherReason.trim()) ||
                  !finalReason
                }
                onClick={handleCancel}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Mark cancelled"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
